import { Notification } from "@/utils/notificationUtils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CreateUpdateBailAccountSettings } from "../../../../modules/commissions/bailaccount/types";
import { commissionsSettingsLevelApi } from "../../../../modules/commissions/level/api";
import {
  CommissionsSettingsLevel,
  CommissionsSettingsLevelFilterPageRequest,
  CreateUpdateCommissionsSettingsLevel,
  CreateUpdateCommissionsSettingsLevelRule,
} from "../../../../modules/commissions/level/types";
import { EnumerationType } from "../../../../modules/enumerations/enums";
import { UUID } from "../../../../typings/global";
import { EntityObject, TwoLevelEntityIdObject, TwoLevelEntityObject } from "../../../types";
import { invalidateEnumeration } from "../enumerations";
import { COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS } from "./queryKeys";

export const useFilterCommissionsSettingsLevels = (payload: CommissionsSettingsLevelFilterPageRequest) => {
  return useQuery({
    queryKey: COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.filterCommissionsSettingsLevels(payload),
    queryFn: async () => {
      const { data } = await commissionsSettingsLevelApi.filterCommissionsLevels(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useGetCommissionsSettingsLevel = (id: string) => {
  return useQuery({
    queryKey: COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.getCommissionsSettingsLevel(id),
    queryFn: async () => {
      const { data } = await commissionsSettingsLevelApi.getCommissionsLevel({ id });

      return data;
    },
  });
};

export const useCreateCommissionsSettingsLevel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.createCommissionsSettingsLevel(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUpdateCommissionsSettingsLevel) => {
      const { data } = await commissionsSettingsLevelApi.createCommissionsLevel(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.COMMISSIONS_SETTINGS_LEVELS);
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.filterCommissionsSettingsLevels(),
      });

      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateCommissionsSettingsLevel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.updateCommissionsSettingsLevel(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateCommissionsSettingsLevel>) => {
      const { data } = await commissionsSettingsLevelApi.updateCommissionsLevel(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      invalidateEnumeration(EnumerationType.COMMISSIONS_SETTINGS_LEVELS);
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.filterCommissionsSettingsLevels(),
      });
      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.getCommissionsSettingsLevel(mutatedPayload.id),
        data,
      );
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteCommissionsSettingsLevel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.deleteCommissionsSettingsLevel(),
    mutationFn: (id: UUID) => {
      return commissionsSettingsLevelApi.deleteCommissionsLevel({ id });
    },
    onSuccess: (_, mutatedPayload) => {
      invalidateEnumeration(EnumerationType.COMMISSIONS_SETTINGS_LEVELS);
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.filterCommissionsSettingsLevels(),
      });
      queryClient.removeQueries({
        queryKey: COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.getCommissionsSettingsLevel(mutatedPayload),
      });
      Notification.itemDeletedNotification();
    },
  });
};

export const useCreateCommissionsLevelRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.createCommissionsLevelRule(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateCommissionsSettingsLevelRule>) => {
      const { data } = await commissionsSettingsLevelApi.createCommissionsLevelRule(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.getCommissionsSettingsLevel(mutatedPayload.id),
        (oldData: CommissionsSettingsLevel): CommissionsSettingsLevel => ({
          ...oldData,
          rules: [...oldData.rules, data],
        }),
      );
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateCommissionsLevelRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.updateCommissionsLevelRule(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<CreateUpdateCommissionsSettingsLevelRule>) => {
      const { data } = await commissionsSettingsLevelApi.updateCommissionsLevelRule(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.getCommissionsSettingsLevel(mutatedPayload.id1),
        (oldData: CommissionsSettingsLevel): CommissionsSettingsLevel => ({
          ...oldData,
          rules: oldData.rules.map((rule) => (rule.id === mutatedPayload.id2 ? data : rule)),
        }),
      );
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteCommissionsLevelRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.deleteCommissionsLevelRule(),
    mutationFn: async (payload: TwoLevelEntityIdObject) => {
      return commissionsSettingsLevelApi.deleteCommissionsLevelRule(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.getCommissionsSettingsLevel(mutatedPayload.id1),
        (oldData: CommissionsSettingsLevel): CommissionsSettingsLevel => ({
          ...oldData,
          rules: oldData.rules.filter((rule) => rule.id !== mutatedPayload.id2),
        }),
      );
      Notification.itemDeletedNotification();
    },
  });
};

export const useCreateCommissionsLevelBailAccountSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.createCommissionsLevelBailAccountSettings(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateBailAccountSettings>) => {
      const { data } = await commissionsSettingsLevelApi.createCommissionsLevelBailAccountSettings(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.getCommissionsSettingsLevel(mutatedPayload.id),
        (oldData: CommissionsSettingsLevel): CommissionsSettingsLevel => ({
          ...oldData,
          bailAccountSettings: data,
        }),
      );
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateCommissionsLevelBailAccountSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.updateCommissionsLevelBailAccountSettings(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateBailAccountSettings>) => {
      const { data } = await commissionsSettingsLevelApi.updateCommissionsLevelBailAccountSettings(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.getCommissionsSettingsLevel(mutatedPayload.id),
        (oldData: CommissionsSettingsLevel): CommissionsSettingsLevel => ({
          ...oldData,
          bailAccountSettings: data,
        }),
      );
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteCommissionsLevelBailAccountSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.deleteCommissionsLevelBailAccountSettings(),
    mutationFn: (id: UUID) => {
      return commissionsSettingsLevelApi.deleteCommissionsLevelBailAccountSettings({ id });
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.getCommissionsSettingsLevel(mutatedPayload),
        (oldData: CommissionsSettingsLevel): CommissionsSettingsLevel => ({
          ...oldData,
          bailAccountSettings: undefined,
        }),
      );
      Notification.itemDeletedNotification();
    },
  });
};

export const useUploadCommissionsLevelAttachments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.uploadCommissionsLevelAttachments(),
    mutationFn: async (payload: EntityObject<FormData>) => {
      const { data } = await commissionsSettingsLevelApi.uploadCommissionsLevelAttachments(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.getCommissionsSettingsLevel(mutatedPayload.id),
        (oldData: CommissionsSettingsLevel): CommissionsSettingsLevel => ({
          ...oldData,
          attachments: data,
        }),
      );
    },
  });
};

export const useDeleteCommissionsLevelAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.deleteCommissionsLevelAttachment(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return commissionsSettingsLevelApi.deleteCommissionsLevelAttachment(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_LEVELS_QUERY_KEYS.getCommissionsSettingsLevel(mutatedPayload.id1),
        (oldData: CommissionsSettingsLevel): CommissionsSettingsLevel => ({
          ...oldData,
          attachments: oldData.attachments.filter((attachment) => attachment.id !== mutatedPayload.id2),
        }),
      );
    },
  });
};
